import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Tooltip,
  Divider,
} from '@mui/material';
import dayjs from 'dayjs';
import StatusChipFinanceiro from './StatusChipFinanceiro';
import { formatCurrency } from '../../utils/money';
import type { PedidoReembolso } from '../../types/pedidoReembolso';

// Componente compartilhado entre PedidosReembolsoPage (organizador/admin) e ReembolsosTab
// (atleta) — mora em components/financeiro/ por construção (ai/anti-patterns.md §2
// "Cross-Page Imports"; financeiro-dashboard-atleta Phase 5, Task 3 do plano). Movido para cá a
// partir da antiga pasta de página do reembolso do organizador, sem alterar a interface de props
// nem o comportamento: só o formatCurrency local foi trocado pelo canônico de utils/money e o
// RefundStatusChip pelo StatusChipFinanceiro unificado da Phase 3.

export interface PedidoReembolsoCardProps {
  pedido: PedidoReembolso;
  isOrganizador: boolean;
  isAdmin: boolean;
  canExecute: boolean;
  executeDisabledReason: string | null;
  onAprovar: () => void;
  onRecusar: () => void;
  onExecutar: () => void;
  onFinalizar: () => void;
  onHistorico: () => void;
}

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  return dayjs(value).format('DD/MM/YYYY HH:mm');
};

const PedidoReembolsoCard: React.FC<PedidoReembolsoCardProps> = ({
  pedido,
  isOrganizador,
  isAdmin,
  canExecute,
  executeDisabledReason,
  onAprovar,
  onRecusar,
  onExecutar,
  onFinalizar,
  onHistorico,
}) => {
  const canManage = isOrganizador || isAdmin;
  const isPendente = pedido.status === 'PENDENTE';
  const isAprovado = pedido.status === 'APROVADO';
  const isTerminal = pedido.status === 'RECUSADO' || pedido.status === 'FINALIZADO';

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              Pedido #{pedido.id}
            </Typography>
            <StatusChipFinanceiro status={pedido.status} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {pedido.valorTotal == null ? '-' : formatCurrency(pedido.valorTotal)}
          </Typography>
        </Box>

        {/* Info grid */}
        <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
          <Box sx={{ minWidth: 200, flex: '1 1 30%' }}>
            <Typography variant="caption" color="text.secondary">Motivo da Solicitacao</Typography>
            <Typography variant="body2">{pedido.motivoSolicitacao || '-'}</Typography>
          </Box>

          {pedido.motivoRecusa && (
            <Box sx={{ minWidth: 200, flex: '1 1 30%' }}>
              <Typography variant="caption" color="text.secondary">Motivo da Recusa</Typography>
              <Typography variant="body2" color="error">{pedido.motivoRecusa}</Typography>
            </Box>
          )}

          {pedido.observacaoOrganizador && (
            <Box sx={{ minWidth: 200, flex: '1 1 30%' }}>
              <Typography variant="caption" color="text.secondary">Observacao do Organizador</Typography>
              <Typography variant="body2">{pedido.observacaoOrganizador}</Typography>
            </Box>
          )}

          <Box sx={{ minWidth: 140, flex: '1 1 20%' }}>
            <Typography variant="caption" color="text.secondary">Meio de Pagamento</Typography>
            <Typography variant="body2">{pedido.meioPagamentoOriginal || '-'}</Typography>
          </Box>

          <Box sx={{ minWidth: 140, flex: '1 1 20%' }}>
            <Typography variant="caption" color="text.secondary">Invoice</Typography>
            <Typography variant="body2">{pedido.invoiceId ? 'Sim' : 'Nao'}</Typography>
          </Box>

          {pedido.iuguRefundId && (
            <Box sx={{ minWidth: 200, flex: '1 1 30%' }}>
              <Typography variant="caption" color="text.secondary">ID Refund (Gateway)</Typography>
              <Typography variant="body2">{pedido.iuguRefundId}</Typography>
            </Box>
          )}
        </Box>

        {/* Metadata */}
        <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
          <Typography variant="caption" color="text.secondary">
            Solicitado por: {pedido.criadoPor?.nome || '-'} em {formatDate(pedido.criadoEm)}
          </Typography>
          {pedido.atualizadoPor && (
            <Typography variant="caption" color="text.secondary">
              | Atualizado por: {pedido.atualizadoPor.nome} em {formatDate(pedido.atualizadoEm)}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Actions */}
        <Box display="flex" gap={1} flexWrap="wrap">
          {/* PENDENTE: Aprovar / Recusar */}
          {isPendente && canManage && (
            <>
              <Button variant="contained" color="primary" size="small" onClick={onAprovar}>
                Aprovar
              </Button>
              <Button variant="outlined" color="error" size="small" onClick={onRecusar}>
                Recusar
              </Button>
            </>
          )}

          {/* APROVADO: Executar (only organizador) / Finalizar / Recusar */}
          {isAprovado && canManage && (
            <>
              {isOrganizador && (
                <Tooltip title={executeDisabledReason || ''} arrow>
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={onExecutar}
                      disabled={!canExecute}
                    >
                      Executar Reembolso
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button variant="outlined" color="primary" size="small" onClick={onFinalizar}>
                Finalizar Manualmente
              </Button>
              <Button variant="outlined" color="error" size="small" onClick={onRecusar}>
                Recusar
              </Button>
            </>
          )}

          {/* Athlete view for non-terminal */}
          {!canManage && isPendente && (
            <Typography variant="body2" color="text.secondary">
              Pedido em analise pelo organizador.
            </Typography>
          )}
          {!canManage && isAprovado && (
            <Typography variant="body2" color="text.secondary">
              Pedido aprovado, aguardando execucao do reembolso.
            </Typography>
          )}

          {/* Terminal states: info only */}
          {isTerminal && pedido.status === 'FINALIZADO' && (
            <Typography variant="body2" color="success.main">
              Reembolso finalizado.
            </Typography>
          )}
          {isTerminal && pedido.status === 'RECUSADO' && (
            <Typography variant="body2" color="error">
              Pedido recusado.
            </Typography>
          )}

          {/* History button always visible */}
          <Button variant="text" size="small" onClick={onHistorico} sx={{ ml: 'auto' }}>
            Historico
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PedidoReembolsoCard;
